import { EmailTemplate, RelatedEntityType } from "@dune/types";
import {
	previewEmailTemplate,
	useGetEmailTemplatesByEntity,
} from "../api/queries/email-templates.queries";
import { usePropositionTemplateData } from "./usePropositionTemplateData";

interface UseEmailTemplatesForEntityProps {
	entityType: RelatedEntityType;
	entityId?: string;
}

export const useEmailTemplatesForEntity = ({
	entityType,
	entityId,
}: UseEmailTemplatesForEntityProps) => {
	const {
		data: templates,
		isLoading,
		error,
	} = useGetEmailTemplatesByEntity(entityType);

	const isProposition = entityType === RelatedEntityType.PROPOSITION;
	const { processEmailTemplate } = usePropositionTemplateData({
		propositionId: isProposition && entityId ? Number(entityId) : 0,
		enabled: isProposition && !!entityId,
	});

	const loadTemplateWithData = async (
		templateId: string,
	): Promise<{
		subject: string;
		htmlContent: string;
		template: EmailTemplate;
	}> => {
		const template = templates?.find((t) => t.id === templateId);
		if (!template) {
			throw new Error(`Template with ID ${templateId} not found`);
		}

		if (!entityId) {
			// Return template without data replacement if no entityId provided
			return {
				subject: template.subject,
				htmlContent: template.htmlContent,
				template,
			};
		}

		try {
			// For propositions, use the new frontend-based placeholder replacement
			if (entityType === RelatedEntityType.PROPOSITION) {
				const processedTemplate = processEmailTemplate(
					template.subject,
					template.htmlContent,
				);

				return {
					subject: processedTemplate.subject,
					htmlContent: processedTemplate.htmlContent,
					template,
				};
			}

			// For brokers and other entity types, use the backend preview API
			const previewData = await previewEmailTemplate(templateId, entityId);

			return {
				subject: previewData.subject,
				htmlContent: previewData.htmlContent,
				template,
			};
		} catch (error) {
			console.error("Error loading template with data:", error);
			// Fallback to template without data replacement
			return {
				subject: template.subject,
				htmlContent: template.htmlContent,
				template,
			};
		}
	};

	return {
		templates: templates?.filter((t) => t.isActive) || [],
		isLoading,
		error,
		loadTemplateWithData,
	};
};

// Removed useEmailTemplatePreview as it's not needed with the new system
